import type { OrdersResponse, OrdersState } from "~/types/Data/user/order";
import type { BaseResponse } from "~/types/Data/Common/APIs";
import useCart from "~/composables/checkout/useCart";
import paths from "~/constants/paths";
import { CartResponse } from "~/types/Data/checkout/Cart";
import { OrderCellProps } from "~/types/schemas/product/Organism/orderCell";

export default function useOrders() {
  const userOrders = useState<OrdersState>("user-orders", () => ({
    activeOrders: null,
    inActiveOrders: null,
  }));

  // FIXME: we should propagate the original data not schema value unless its absolute necessary
  const selectedOrder = useState<OrderCellProps | undefined>(
    "user-selected-order",
    () => undefined,
  );
  const isCancelModalOpen = useState("is-cancel-modal-open", () => false);
  const isTrackModalOpen = useState("is-track-modal-open", () => false);

  const loading = ref(false);

  const { cart } = useCart();
  const { addToast } = useToast();
  const nuxtApp = useNuxtApp();
  const router = useRouter();
  const userToken = useCookie("hs-token");

  const fetchActiveOrders = async (baseUrl?: string) => {
    loading.value = true;
    try {
      const { res } = await useApi<OrdersResponse>(
        `/api/v1/orders?filter[is_active]=1&include=variants,statuses,bundles,products`,
        {
          authToken: userToken.value,
        },
        {
          baseUrl,
        },
      );
      userOrders.value.activeOrders = res?.data ? res.data : [];
    } finally {
      loading.value = false;
    }
  };

  const fetchInActiveOrders = async (baseUrl?: string) => {
    loading.value = true;
    try {
      const { res } = await useApi<OrdersResponse>(
        `/api/v1/orders?filter[is_active]=0&include=variants,statuses,bundles,products`,
        {
          authToken: userToken.value,
        },
        {
          baseUrl,
        },
      );
      userOrders.value.inActiveOrders = res?.data ? res.data : [];
    } finally {
      loading.value = false;
    }
  };

  const cancelOrder = async (id: string) => {
    loading.value = true;

    try {
      await useApi(`/api/v1/cancel-order/${id}`, {
        method: "PUT",
        authToken: userToken.value,
      });

      addToast("Canceled!", "success");

      await fetchActiveOrders(nuxtApp.$baseUrl);
      await fetchInActiveOrders(nuxtApp.$baseUrl);

      isCancelModalOpen.value = false;
      selectedOrder.value = undefined;
    } catch {
      addToast("Failed to cancel order!", "error");
    }

    loading.value = false;
  };

  const orderAgain = async (orderId: string) => {
    loading.value = true;

    try {
      const { res, err } = await useApi<CartResponse>(
        `/api/v1/orders/${orderId}/order-again`,
        {
          method: "POST",
          authToken: userToken.value,
        },
        {
          catchError: true,
        },
      );

      if (res) {
        cart.value = res.data;

        const hasSkippedItems = res.data.has_skipped_items;
        const totalQuantity = res.data.total_quantity;

        if (totalQuantity === 0) {
          addToast("All Items Are Out of Stock", "error");
        } else if (hasSkippedItems) {
          addToast("In Stock Items Added to Cart", "success");
          router.push(paths.cart);
        } else {
          addToast("Items added to cart successfully!", "success");
          router.push(paths.cart);
        }
      } else {
        addToast(err?.meta.errors[0].message ?? "", "error");
      }
    } finally {
      loading.value = false;
    }
  };

  const downloadOrderInvoice = async (orderId: string) => {
    loading.value = true;
    try {
      const { res } = await useApi<BaseResponse<string>>(
        `/api/v1/download-invoice/${orderId}`,
      );

      if (res?.data) {
        return res.data;
      } else {
        addToast("Failed to download invoice", "error");
      }
    } finally {
      loading.value = false;
    }
  };

  return {
    loading,
    userOrders,
    selectedOrder,
    isTrackModalOpen,
    isCancelModalOpen,
    orderAgain,
    cancelOrder,
    fetchActiveOrders,
    fetchInActiveOrders,
    downloadOrderInvoice,
  };
}
